import React from "react";
import PropTypes from "prop-types";
import styled, { createGlobalStyle, css } from "styled-components/macro";
import { MdClose } from "react-icons/md";

import Card from "./Card";
import Button from "./Button";

const glassVisibleClass = "rui-modal-glass";

const GlobalStyle = createGlobalStyle`
  :root[class*=${glassVisibleClass}] {
    max-height: 100%;
    overflow: hidden;
  }
`;

const glassVisibleStyles = css`
  transition: background 0.3s;
  background: rgba(0, 0, 0, 0.5);
  visibility: visible;

  & > * {
    transition: opacity 0.3s, transform 0.3s;
    opacity: 1;
    transform: none;
  }
`;

const glassInvisibleStyles = css`
  transition: background 0.3s, visibility 0s 0.3s;
  background: rgba(0, 0, 0, 0);
  visibility: hidden;
  z-index: -1;

  & > * {
    transition: opacity 0.3s, transform 0.3s;
    opacity: 0;
    transform: translateY(-50px);
  }
`;

const Glass = styled.div`
  cursor: default;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
  padding-bottom: 10px;
  ${(props) => (props.visible ? glassVisibleStyles : glassInvisibleStyles)};
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DefaultHeader = (props) => (
  <React.Fragment>
    <div>{props.title}</div>
    <MdClose onClick={() => props.onCloseButtonClicked && props.onCloseButtonClicked()} style={{ cursor: "pointer" }} />
  </React.Fragment>
);

const DefaultFooter = (props) => (
  <React.Fragment>
    <Button
      onClick={() => props.onSecondaryButtonClicked && props.onSecondaryButtonClicked()}
      children={props.secondaryButton}
    />
    <Button
      onClick={() => props.onPrimaryButtonClicked && props.onPrimaryButtonClicked()}
      primary
      disabled={props.isPrimaryButtonDisabled}
      children={props.primaryButton}
    />
  </React.Fragment>
);

const ModalCard = styled(Card)`
  width: calc(100% - 200px);
  max-width: ${(props) => (props.fullWidth ? "100%" : props.wide ? "800px" : "600px")};

  @media screen and (max-width: ${(props) => (props.fullWidth ? "1200px" : props.wide ? "900px" : "800px")}) {
    width: 100%;
    max-width: 100%;
  }
`;

class Modal extends React.Component {
  static modalId = 0;

  constructor(props) {
    super(props);
    this.documentElementClassName = `${glassVisibleClass}-${Modal.modalId++}`;
  }

  componentDidMount() {
    this.props.visible ? this.makeGlobalSideEffects() : this.revertGlobalSideEffects();
  }

  componentDidUpdate() {
    this.props.visible ? this.makeGlobalSideEffects() : this.revertGlobalSideEffects();
  }

  componentWillUnmount() {
    this.revertGlobalSideEffects();
  }

  makeGlobalSideEffects() {
    document.body.addEventListener("keyup", this.closeOnEscape);
    document.documentElement.classList.add(this.documentElementClassName);
  }

  revertGlobalSideEffects() {
    document.body.removeEventListener("keyup", this.closeOnEscape);
    document.documentElement.classList.remove(this.documentElementClassName);
  }

  closeOnEscape = (event) => {
    const formElements = ["INPUT", "TEXTAREA"];

    if (event.key === "Escape" && !formElements.includes(event.target.tagName)) {
      this.props.onCloseButtonClicked && this.props.onCloseButtonClicked();
    }
  };

  render() {
    const header = this.props.header !== null && (
      <HeaderWrapper children={this.props.header || <DefaultHeader {...this.props} />} />
    );

    const footer = this.props.footer !== null && (
      <FooterWrapper children={this.props.footer || <DefaultFooter {...this.props} />} />
    );

    return (
      <Glass visible={this.props.visible} className={this.props.className}>
        <GlobalStyle />
        <ModalCard
          role="dialog"
          aria-label={this.props.title}
          aria-modal={true}
          block
          compact={this.props.compact}
          wide={this.props.wide}
          fullWidth={this.props.fullWidth}
          header={header}
          footer={footer}
          children={this.props.children}
        />
      </Glass>
    );
  }
}

Modal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onSecondaryButtonClicked: PropTypes.func,
  onPrimaryButtonClicked: PropTypes.func,
  onCloseButtonClicked: PropTypes.func,
  primaryButton: PropTypes.string,
  secondaryButton: PropTypes.string,
  isPrimaryButtonDisabled: PropTypes.bool,
  title: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
  compact: PropTypes.bool,
  wide: PropTypes.bool,
  fullWidth: PropTypes.bool,
  scrollable: PropTypes.bool,
};

Modal.defaultProps = {
  primaryButton: "OK",
  secondaryButton: "Cancel",
};

export default Modal;
